import PropTypes from 'prop-types';
import React from 'react';
import { Status, Tooltip, Icon } from '@weezevent/nacre';
import moment from 'moment';

import { useTranslation } from 'react-i18next';

export function StatusComponent({ title, color = 'green', tooltip, displayClock = false }) {
    return (
        <Tooltip position="bottom" triggerOnHover={true} title={tooltip || title} className={'tooltip-list-status'}>
            <Status style={{ marginRight: '5px' }} text={title} color={color} />
            {displayClock ? <Status color="purple" text={<Icon margin={false} name="time" />} /> : null}
        </Tooltip>
    );
}
StatusComponent.propTypes = {
    title: PropTypes.string,
    color: PropTypes.string,
    tooltip: PropTypes.string,
    displayClock: PropTypes.bool
};

export function RateStatus({ rate, settings, event, quotas = [] }) {
    const { t } = useTranslation();

    let data = React.useMemo(() => {
        const rateQuota = quotas.find(quota => quota.rate_id === rate.id && quota.event_id === event.id && quota.session_id === 0);
        const eventQuota = quotas.find(quota => quota.event_id === event.id && quota.rate_id === 0 && quota.session_id === 0);

        if (eventQuota && eventQuota.quota_total !== null && eventQuota.quota_total <= eventQuota.quota_taken) {
            return { title: t('ticket.rates.status.global_quota_reached'), color: 'grey', tooltip: t('ticket.rates.statusOnHover.isEventQuotaReached'), displayClock: false };
        }
        if (rateQuota && rateQuota.quota_total !== null && rateQuota.quota_total <= rateQuota.quota_taken) {
            return { title: t('ticket.rates.status.sold_out'), color: 'red', tooltip: t('ticket.rates.statusOnHover.isRateQuotaReached'), displayClock: false };
        }

        if (rate.disabled) {
            // Hidden
            if (rate.visibility_start_date || rate.relays.length === 1) {
                return {
                    title: t('ticket.rates.status.hidden'),
                    color: 'orange',
                    tooltip: t('ticket.rates.statusOnHover.hiddenAndScheduled'),
                    displayClock: Boolean(rate.relays.length)
                };
            }
            return { title: t('ticket.rates.status.hidden'), color: 'orange', tooltip: t('ticket.rates.statusOnHover.hidden'), displayClock: false };
        }

        const nowDate = moment.utc();
        let nowDateMoreOneHours = moment.utc();
        nowDateMoreOneHours.add(1, 'hours');
        const startDate = rate.visibility_start_date ? moment.utc(rate.visibility_start_date) : null;
        const endDate = rate.visibility_end_date ? moment.utc(rate.visibility_end_date) : null;
        const globalStartDate = settings?.start_sale ? moment.utc(settings.start_sale) : null;
        const globalEndDate = settings?.end_sale ? moment.utc(settings.end_sale) : null;
        if (rate.relays?.length === 1) {
            // Manage States with relays
            const [relay] = rate.relays;
            let isRateOriginRelayQuotaReached = false;
            const rateOriginQuota = quotas.find(quota => quota.rate_id === relay.id);
            if (rateOriginQuota && rateOriginQuota.quota_total !== null && rateOriginQuota.quota_total <= rateOriginQuota.quota_taken) {
                isRateOriginRelayQuotaReached = true;
            }
            // TODO Here we do not have information about quotas goal of relay.
            const canSell = relay.disabled || moment.utc(relay.visibility_end_date) < nowDate ? true : false || isRateOriginRelayQuotaReached;

            // TODO Here we do not have the rate quotas rate goal eather.
            const outOfGoal = endDate && endDate < nowDate ? true : false;
            if (canSell && !outOfGoal) {
                // onSale with relays
                return {
                    title: t('ticket.rates.status.onSale'),
                    color: 'green',
                    tooltip: t('ticket.rates.statusOnHover.onSaleFromRelay', { rateRelayName: relay.name }),
                    displayClock: true
                };
            }
            if (!outOfGoal && !canSell) {
                // Scheduled with relays
                return {
                    title: t('ticket.rates.status.relay_rate'),
                    color: 'purple',
                    tooltip: t('ticket.rates.statusOnHover.scheduledWithRelays', { rateRelayName: relay.name }),
                    displayClock: false
                };
            }
            // Sale ended
            return { title: t('ticket.rates.status.ended'), color: 'grey', tooltip: t('ticket.rates.statusOnHover.endedRelay'), displayClock: false };
        }

        if (startDate || endDate) {
            // Manage States with start / end date
            if (startDate && nowDate < startDate) {
                // Scheduled with date.
                return {
                    title: t('ticket.rates.status.scheduled'),
                    color: 'purple',
                    tooltip: t('ticket.rates.statusOnHover.scheduledWithDate', { date: startDate.toLocaleString() }),
                    displayClock: false
                };
            }
            if (endDate && nowDateMoreOneHours > endDate && endDate > nowDate) {
                // end of sale close
                return { title: t('ticket.rates.status.onSale'), color: 'green', tooltip: t('ticket.rates.statusOnHover.endClose'), displayClock: false };
            }
            if (
                (startDate && endDate && endDate > nowDate && nowDate > startDate) ||
                (startDate && !endDate && nowDate > startDate) ||
                (!startDate && endDate && endDate > nowDate)
            ) {
                // On sale and was scheduled.
                if (endDate) {
                    return {
                        title: t('ticket.rates.status.onSale'),
                        color: 'green',
                        tooltip: t('ticket.rates.statusOnHover.onSaleWithEnd', { date: endDate.toLocaleString() }),
                        displayClock: true
                    };
                }
                return {
                    title: t('ticket.rates.status.onSale'),
                    color: 'green',
                    tooltip: t('ticket.rates.statusOnHover.onSale'),
                    displayClock: true
                };
            }

            // Ended sales.
            return {
                title: t('ticket.rates.status.ended'),
                color: 'grey',
                tooltip: t('ticket.rates.statusOnHover.ended', { date: endDate.toLocaleString() }),
                displayClock: false
            };
        }

        if (globalStartDate || globalEndDate) {
            // Manage States with golbal start date or global end date
            if (nowDate < globalStartDate) {
                // Scheduled.
                return { title: t('ticket.rates.status.scheduled'), color: 'purple', tooltip: t('ticket.rates.statusOnHover.scheduledWithGlobalDate'), displayClock: false };
            }
            if (
                (globalStartDate && globalEndDate && globalEndDate > nowDate > globalStartDate) ||
                (globalStartDate && !globalEndDate && nowDate > globalStartDate) ||
                (!globalStartDate && globalEndDate && globalEndDate > nowDate)
            ) {
                // On sale.
                return { title: t('ticket.rates.status.onSale'), color: 'green', tooltip: t('ticket.rates.statusOnHover.onSale'), displayClock: false };
            }
            if (globalEndDate && globalEndDate < nowDate) {
                // Sale ended
                return {
                    title: t('ticket.rates.status.ended'),
                    color: 'grey',
                    tooltip: t('ticket.rates.statusOnHover.endedWithGlobalDate', { date: globalEndDate.toLocaleString() }),
                    displayClock: false
                };
            }
        }
        if (event?.publication_status !== 'published') {
            // On sale when organizer will publish event
            return { title: t('ticket.rates.status.onSale'), color: 'green', tooltip: t('ticket.rates.statusOnHover.onSaleWhenPublish'), displayClock: false };
        }

        // On sale
        return { title: t('ticket.rates.status.onSale'), color: 'green', tooltip: t('ticket.rates.statusOnHover.onSale'), displayClock: false };
    }, [event, rate, rate.disabled, settings, quotas]);

    return <StatusComponent {...data} />;
}

RateStatus.propTypes = {
    rate: PropTypes.object,
    settings: PropTypes.object,
    event: PropTypes.object
};
